const DAY_MS = 86400000;

/**
 * @function toMillis
 * @description Normalizes a Date or a numeric timestamp to milliseconds, so both shapes can be
 * compared directly.
 * @param {Date|number} time - The value to normalize.
 * **Called by:
 * @see {mergeByTimeProximity}
 * @memberof module:ShoreS
 * @returns {number} Milliseconds since the epoch.
 */
const toMillis = (time) => (time instanceof Date ? time.getTime() : Number(time));

/**
 * @function withinTolerance
 * @description Checks whether two timestamps fall inside the matching window.
 * @param {number} a - First timestamp, in milliseconds.
 * @param {number} b - Second timestamp, in milliseconds.
 * @param {number} toleranceMs - The matching window, in milliseconds.
 * **Called by:
 * @see {mergeByTimeProximity}
 * @memberof module:ShoreS
 * @returns {boolean} True when the gap is within tolerance.
 */
const withinTolerance = (a, b, toleranceMs) => Math.abs(a - b) <= toleranceMs;

/**
 * @function groupByTransect
 * @description Buckets distance rows by `transectId`, so the merge only ever compares records
 * measured on the same transect.
 * @param {Array<Object>} rows - Distance records.
 * **Called by:
 * @see {mergeByTimeProximity}
 * @memberof module:ShoreS
 * @returns {Map} Map of transectId to its records.
 */
const groupByTransect = (rows) => {
  const map = new Map();
  rows.forEach((row) => {
    if (!map.has(row.transectId)) map.set(row.transectId, []);
    map.get(row.transectId).push(row);
  });
  return map;
};

/**
 * @constant MERGE_TOLERANCE_DAYS
 * @description Default matching window, in days, for pairing modeled and reference distances.
 * @memberof module:ShoreS
 */
export const MERGE_TOLERANCE_DAYS = 30;

/**
 * @constant MERGE_DIRECTION
 * @description Default search direction when looking for the closest-in-time reference.
 * @memberof module:ShoreS
 */
export const MERGE_DIRECTION = 'forward';

/**
 * @function mergeByTimeProximity
 * @description Pairs each modeled distance with the closest-in-time reference distance
 * for the same transect, within a tolerance window, per `direction`:
 *  - 'forward': first reference row with time >= modeled time
 *  - 'backward': last reference row with time <= modeled time
 *  - 'nearest': reference row with the smallest absolute time difference
 *
 * Both `modeledDistances` and `referenceDistances` are grouped by
 * `transectId` and sorted by time before matching.
 * @param {Array<{transectId, time: Date, distance: number|null}>} modeledDistances
 * @param {Array<{transectId, time: Date, distance: number|null}>} referenceDistances
 * @param {{toleranceInDays: (number|undefined), direction: (string|undefined)}} [options] - `direction` is one of `'forward'`, `'backward'` or `'nearest'`.
 * **Called by:
 * @see {shoreSAnalysis/saga}
 * @memberof module:ShoreS
 * @returns {Array<{transectId, time: Date, distanceModeled: number|null, timeReference: Date|null, distanceReference: number|null}>}
 */
export const mergeByTimeProximity = (modeledDistances, referenceDistances, options = {}) => {
  const { toleranceInDays = MERGE_TOLERANCE_DAYS, direction = MERGE_DIRECTION } = options;
  const toleranceMs = toleranceInDays * DAY_MS;

  const modeledByTransect = groupByTransect(modeledDistances);
  const referenceByTransect = groupByTransect(referenceDistances);

  const merged = [];

  modeledByTransect.forEach((modeledRows, transectId) => {
    const sortedModeled = [...modeledRows].sort((a, b) => toMillis(a.time) - toMillis(b.time));
    const sortedReference = [...(referenceByTransect.get(transectId) || [])].sort(
      (a, b) => toMillis(a.time) - toMillis(b.time),
    );

    // Monotonic pointer shared across this transect's modeled rows — valid
    // because modeled times are processed in ascending order, so the
    // earliest acceptable reference index can only move forward.
    let pointer = 0;

    sortedModeled.forEach((modeledRow) => {
      const modeledMs = toMillis(modeledRow.time);
      let matched = null;

      if (direction === 'forward') {
        while (pointer < sortedReference.length && toMillis(sortedReference[pointer].time) < modeledMs) {
          pointer += 1;
        }
        const candidate = sortedReference[pointer];
        if (candidate && withinTolerance(toMillis(candidate.time), modeledMs, toleranceMs)) {
          matched = candidate;
        }
      } else if (direction === 'backward') {
        while (
          pointer + 1 < sortedReference.length &&
          toMillis(sortedReference[pointer + 1].time) <= modeledMs
        ) {
          pointer += 1;
        }
        const candidate = sortedReference[pointer];
        if (
          candidate &&
          toMillis(candidate.time) <= modeledMs &&
          withinTolerance(toMillis(candidate.time), modeledMs, toleranceMs)
        ) {
          matched = candidate;
        }
      } else {
        // 'nearest' — group sizes here are small enough that a full scan is fine.
        let best = null;
        let bestDiff = Infinity;
        sortedReference.forEach((candidate) => {
          const diff = Math.abs(toMillis(candidate.time) - modeledMs);
          if (diff < bestDiff) {
            best = candidate;
            bestDiff = diff;
          }
        });
        if (best && withinTolerance(toMillis(best.time), modeledMs, toleranceMs)) {
          matched = best;
        }
      }

      merged.push({
        transectId,
        time: modeledRow.time,
        distanceModeled: modeledRow.distance,
        timeReference: matched ? matched.time : null,
        distanceReference: matched ? matched.distance : null,
      });
    });
  });

  return merged;
};
